import { detectBrandKey, searchRadiusForCountry } from './retailEngine'
import { createPlacesService } from './maps'
import type { NearbyStore } from './storeTypes'

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const DEFAULT_CHAIN_CATEGORIES: Record<string, string[]> = {
  hofer: ['budget', 'fresh', 'dairy', 'produce'],
  billa: ['fruits', 'vegetables', 'dairy', 'meat', 'produce'],
  lidl_at: ['budget', 'frozen', 'dry_goods', 'carbs'],
  lidl: ['budget', 'frozen', 'dry_goods', 'carbs'],
  checkers: ['fruits', 'vegetables', 'dairy', 'meat', 'frozen', 'produce'],
  pick_n_pay: ['fruits', 'vegetables', 'dairy', 'meat', 'frozen', 'produce'],
  woolworths_za: ['premium', 'organic', 'fresh', 'meat'],
  spar_za: ['fruits', 'vegetables', 'dairy', 'meat', 'frozen', 'produce'],
  spar_zw: ['fruits', 'vegetables', 'dairy', 'meat', 'frozen', 'produce'],
  spar: ['fruits', 'vegetables', 'dairy', 'meat', 'frozen', 'produce'],
  tm_pick_n_pay: ['fruits', 'vegetables', 'dairy', 'meat', 'produce'],
  bon_marche: ['fruits', 'vegetables', 'dairy', 'meat', 'produce'],
  food_lovers_zw: ['produce', 'organic', 'fresh', 'vegetables'],
}

function placeToStore(
  place: google.maps.places.PlaceResult,
  originLat: number,
  originLng: number,
  allowedBrandKeys: string[],
): NearbyStore | null {
  const brandKey = detectBrandKey(place.name ?? '', allowedBrandKeys)
  if (!brandKey || !place.geometry?.location) return null

  const lat = place.geometry.location.lat()
  const lng = place.geometry.location.lng()

  return {
    name: place.name ?? brandKey,
    chain: brandKey.toUpperCase(),
    brand_key: brandKey,
    address: place.vicinity ?? place.formatted_address ?? '',
    distance_km: Math.round(haversineKm(originLat, originLng, lat, lng) * 10) / 10,
    lat,
    lng,
    place_id: place.place_id,
  }
}

function nearbySearch(
  service: google.maps.places.PlacesService,
  request: google.maps.places.PlaceSearchRequest,
): Promise<google.maps.places.PlaceResult[]> {
  return new Promise((resolve, reject) => {
    service.nearbySearch(request, (results, status) => {
      if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        resolve([])
        return
      }
      if (status !== google.maps.places.PlacesServiceStatus.OK || !results) {
        reject(new Error('Store search failed. Try again in a moment.'))
        return
      }
      resolve(results)
    })
  })
}

export type FindNearbyStoresOptions = {
  countryCode: string
  allowedBrandKeys: string[]
  radiusKm?: number
}

export async function findNearbyStores(
  lat: number,
  lng: number,
  options: FindNearbyStoresOptions,
): Promise<NearbyStore[]> {
  const radiusKm = options.radiusKm ?? searchRadiusForCountry(options.countryCode)
  const service = await createPlacesService()
  const location = new google.maps.LatLng(lat, lng)
  const radiusM = radiusKm * 1000

  const [supermarkets, groceries] = await Promise.all([
    nearbySearch(service, { location, radius: radiusM, type: 'supermarket' }),
    nearbySearch(service, { location, radius: radiusM, type: 'grocery_store' }),
  ])

  const seen = new Set<string>()
  const stores: NearbyStore[] = []

  for (const place of [...supermarkets, ...groceries]) {
    const store = placeToStore(place, lat, lng, options.allowedBrandKeys)
    if (!store) continue
    const key = store.place_id ?? `${store.brand_key}-${store.address}`
    if (seen.has(key)) continue
    seen.add(key)
    stores.push(store)
  }

  return stores.sort((a, b) => (a.distance_km ?? 0) - (b.distance_km ?? 0)).slice(0, 8)
}

function ingredientCategories(ingredientName: string): string[] {
  const key = ingredientName.toLowerCase()
  const cats: string[] = []
  if (/chicken|beef|salmon|fish|turkey|pork|meat|egg|tofu|lentil|chickpea|yogurt|cheese|milk/.test(key)) {
    cats.push('meat', 'dairy', 'protein')
  }
  if (/rice|oats|pasta|bread|granola|quinoa|potato/.test(key)) cats.push('carbs', 'dry_goods', 'budget')
  if (/oil|butter|avocado|nut/.test(key)) cats.push('fats')
  if (/berry|apple|banana|vegetable|spinach|tomato|pepper|broccoli|fruit|produce|salad/.test(key)) {
    cats.push('produce', 'fruits', 'vegetables', 'fresh')
  }
  if (/supplement|vitamin|protein powder/.test(key)) cats.push('pharmacy', 'supplements', 'personal_care')
  if (cats.length === 0) cats.push('produce', 'budget')
  return cats
}

function storeScore(store: NearbyStore, categories: string[]): number {
  const brandKey = store.brand_key ?? store.chain.toLowerCase()
  const chainCats = DEFAULT_CHAIN_CATEGORIES[brandKey] ?? []
  const overlap = chainCats.filter((c) => categories.includes(c)).length
  return overlap * 10 - (store.distance_km ?? 0)
}

export function recommendStore(ingredientName: string, dynamicStores: NearbyStore[]): NearbyStore | null {
  if (dynamicStores.length === 0) return null
  const categories = ingredientCategories(ingredientName)
  const ranked = [...dynamicStores].sort((a, b) => storeScore(b, categories) - storeScore(a, categories))
  return ranked[0] ?? null
}

export function formatStoreLabel(store: NearbyStore): string {
  return `${store.name} (${store.distance_km}km)`
}
